import React, { useState } from 'react';
import HighlightCard from './HighlightCard';
import { BackendCurrency } from '@/utils/currencyHelpers';

interface HighlightLocation {
  name?: string;
  city?: string;
  state?: string;
}

interface Highlight {
  _id?: string;
  title: string;
  description?: string;
  imageUrl?: string[];
  locations?: HighlightLocation;
  isIncluded?: boolean;
  adultPrice?: number;
  childPrice?: number;
}

interface HighlightsListProps {
  highlights: Highlight[];
  backendCurrency?: BackendCurrency;
  title?: string;
}

const HighlightsList: React.FC<HighlightsListProps> = ({
  highlights,
  backendCurrency,
  title = 'Highlights:',
}) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState<{
    [key: string]: boolean;
  }>({});
  const [shouldShowSeeMore, setShouldShowSeeMore] = useState<{
    [key: string]: boolean;
  }>({});

  // Check if descriptions actually overflow when clamped
  const checkDescriptionOverflow = (
    element: HTMLParagraphElement,
    key: string
  ) => {
    if (!element) return;

    // Create a temporary element to measure natural height
    const tempElement = element.cloneNode(true) as HTMLParagraphElement;
    tempElement.className = element.className
      .replace('line-clamp-3', '')
      .trim();
    tempElement.style.visibility = 'hidden';
    tempElement.style.position = 'absolute';
    tempElement.style.width = element.offsetWidth + 'px';
    tempElement.style.top = '-9999px';
    document.body.appendChild(tempElement);

    const naturalHeight = tempElement.scrollHeight;
    document.body.removeChild(tempElement);

    // Calculate expected height for 3 lines (line-height: 17px * 3 = 51px)
    // Add a small buffer for rounding
    const expectedClampedHeight = 17 * 3 + 2;

    // If natural height exceeds expected clamped height, show "see more"
    const hasOverflow = naturalHeight > expectedClampedHeight;

    setShouldShowSeeMore(prev => ({
      ...prev,
      [key]: hasOverflow,
    }));
  };

  const handleToggleDescription = (id: string) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (highlights.length === 0) {
    return null;
  }

  return (
    <div className='mb-0 mt-9'>
      <div className='flex items-center justify-between mb-1'>
        <p className='font-medium text-[12px] leading-6 pt-0.5'>{title}</p>
      </div>
      {/* Flex Layout with Gaps */}
      <div className='flex flex-wrap gap-4 pt-2'>
        {highlights.map((highlight, index) => (
          <HighlightCard
            key={highlight._id || index}
            highlight={highlight}
            index={index}
            backendCurrency={backendCurrency}
            expandedDescriptions={expandedDescriptions}
            shouldShowSeeMore={shouldShowSeeMore}
            onToggleDescription={handleToggleDescription}
            onCheckOverflow={checkDescriptionOverflow}
          />
        ))}
      </div>
    </div>
  );
};

export default HighlightsList;
